/**
 * Modellbefunde gegen den eingefrorenen Diff prüfen — reine Logik.
 *
 * Ein Modell nennt Datei und Zeile mit derselben Bestimmtheit, ob es die
 * Stelle gesehen hat oder nicht. Deshalb entscheidet nicht das Modell, was ein
 * Befund ist, sondern dieser Prüfschritt:
 *
 * - **Struktur** — fehlt die Zeile oder ist die Schwere erfunden, ist es kein
 *   Befund, sondern Text.
 * - **Datei** — sie muss im Diff dieses Laufs vorkommen. Ein Befund über eine
 *   Datei, die nicht Teil des Merge Requests ist, gehört nicht in dieses
 *   Review.
 * - **Zeile** — sie muss in einem Hunk liegen, den der Lauf gezeigt hat. Zeile
 *   800 in einer Datei, deren Diff bei 40 endet, ist geraten; solche Angaben
 *   waren im Bestand der häufigste Grund, warum ein Befund nicht anklickbar
 *   war.
 *
 * Verworfene Kandidaten verschwinden nicht: Der Aufrufer bekommt den Grund und
 * kann ihn zählen. Eine Fehlalarmquote, die niemand misst, sinkt nie.
 */
import { z } from "zod";
import { liegtImDiff, } from "./durchsicht-schnitt.js";
/** Von leicht nach schwer. Die Reihenfolge trägt die Gate-Entscheidung. */
export const SCHWEREGRADE = [
    "hinweis",
    "gering",
    "mittel",
    "schwer",
    "kritisch",
];
/** Diese Grade blockieren ein Quality Gate, sobald es scharf geschaltet ist. */
export const BLOCKIERENDE_SCHWERE = ["schwer", "kritisch"];
export function istBlockierend(schwere) {
    return BLOCKIERENDE_SCHWERE.includes(schwere);
}
/**
 * Was ein Modell liefern muss, damit daraus ein Befund werden kann.
 *
 * `beleg` ist die Zeile aus dem Diff, auf die sich der Befund stützt. Sie ist
 * bei blockierenden Graden Pflicht — siehe `pruefeBefund`.
 */
export const befundVorschlagSchema = z.object({
    datei: z.string().min(1).max(2_048),
    zeileVon: z.number().int().positive(),
    zeileBis: z.number().int().positive().optional(),
    schwere: z.enum(SCHWEREGRADE),
    text: z.string().min(10).max(4_000),
    beleg: z.string().max(2_000).optional(),
    konfidenz: z.number().min(0).max(1).optional(),
    /*
     * Beleg dafür, wie die Stelle beim Fund aussah (CUPR-68). Beide Adapter
     * nehmen ihn vom Modell entgegen und prüfen ihn; ohne Feld hier hat Zod ihn
     * bis jetzt weggeworfen, und `befunde.stellenHash` blieb immer leer. Ohne
     * gefüllte Spalte lässt sich später nicht belegen, dass eine Stelle sich
     * geändert hat — und genau das ist der Unterschied zwischen „behoben" und
     * „diesmal nicht gemeldet".
     *
     * Der Fingerabdruck für Dubletten bleibt davon unberührt: Er nimmt
     * ausdrücklich nur datei, zeileVon und text.
     */
    stellenHash: z.string().trim().min(1).max(500).optional(),
});
export const VERWERFUNGSGRUENDE = [
    "struktur",
    "datei_nicht_im_diff",
    "zeile_nicht_gezeigt",
    "bereich_verdreht",
    "beleg_fehlt",
];
function fnv1a(text) {
    let hash = 0x811c9dc5;
    for (let i = 0; i < text.length; i += 1) {
        hash ^= text.charCodeAt(i);
        hash = Math.imul(hash, 0x01000193) >>> 0;
    }
    return hash.toString(16).padStart(8, "0");
}
/**
 * Servereigener Beleg des Inhalts einer konkreten neuen Diff-Zeile.
 *
 * Anbieterwerte werden dafür bewusst nicht vertraut: Nur ein Hash mit dem
 * Präfix `diff-v1` kann später automatisch als Reparaturnachweis dienen.
 * Kontext- und Pluszeilen zählen, Minuszeilen haben im neuen Stand keine
 * Zeilennummer mehr.
 */
export function stellenHashAusDiff(dateien, pfad, zeile) {
    const datei = dateien.find((eintrag) => eintrag.pfad === pfad);
    if (!datei?.diff || !Number.isSafeInteger(zeile) || zeile < 1)
        return null;
    let neueZeile = null;
    for (const diffZeile of datei.diff.split(/\r?\n/)) {
        const kopf = /^@@ -\d+(?:,\d+)? \+(\d+)(?:,(\d+))? @@/.exec(diffZeile);
        if (kopf) {
            neueZeile = Number(kopf[1]);
            continue;
        }
        if (neueZeile === null)
            continue;
        if (diffZeile.startsWith("-"))
            continue;
        if (diffZeile.startsWith("\\"))
            continue;
        if (!diffZeile.startsWith("+") && !diffZeile.startsWith(" "))
            continue;
        if (neueZeile === zeile) {
            return `diff-v1:${fnv1a(`${pfad}\u0000${diffZeile.slice(1)}`)}`;
        }
        neueZeile += 1;
    }
    return null;
}
/** Welche Zeilen welcher Datei der Lauf gezeigt hat. */
export function baueStellenIndex(dateien, bereicheVon) {
    const index = new Map();
    for (const d of dateien) {
        if (!d.diff)
            continue;
        index.set(d.pfad, bereicheVon(d.diff));
    }
    return index;
}
/**
 * Ein Kandidat wird zum Befund — oder mit Grund verworfen.
 *
 * Blockierende Grade verlangen zusätzlich einen `beleg`. Wer eine Auslieferung
 * aufhalten will, muss die Stelle zitieren können; das ist die billigste
 * Bremse gegen selbstbewusste Erfindungen.
 */
export function pruefeBefund(roh, index) {
    const geprueft = befundVorschlagSchema.safeParse(roh);
    if (!geprueft.success)
        return { ok: false, grund: "struktur", roh };
    const befund = geprueft.data;
    const bereiche = index.get(befund.datei);
    if (!bereiche)
        return { ok: false, grund: "datei_nicht_im_diff", roh };
    const bis = befund.zeileBis ?? befund.zeileVon;
    if (bis < befund.zeileVon)
        return { ok: false, grund: "bereich_verdreht", roh };
    if (!liegtImDiff(bereiche, befund.zeileVon)) {
        return { ok: false, grund: "zeile_nicht_gezeigt", roh };
    }
    if (istBlockierend(befund.schwere) && !befund.beleg?.trim()) {
        return { ok: false, grund: "beleg_fehlt", roh };
    }
    return { ok: true, befund, fingerabdruck: fingerabdruck(befund) };
}
/**
 * Stabiler Fingerabdruck für Dubletten über Läufe hinweg.
 *
 * FNV-1a statt `node:crypto`, weil `@cubic/shared` auch im Browser gebündelt
 * wird. Der Wert braucht keine kryptografische Härte — er muss nur stabil sein
 * und selten kollidieren.
 */
export function fingerabdruck(befund) {
    const norm = `${befund.datei}|${befund.zeileVon}|${befund.text
        .toLowerCase()
        .replace(/\s+/g, " ")
        .trim()}`;
    return fnv1a(norm);
}
/** Alle Kandidaten eines Laufs prüfen und dabei Dubletten zusammenführen. */
export function pruefeBefunde(kandidaten, index) {
    const angenommen = [];
    const verworfen = [];
    const gesehen = new Set();
    let dubletten = 0;
    for (const kandidat of kandidaten) {
        const ergebnis = pruefeBefund(kandidat, index);
        if (!ergebnis.ok) {
            verworfen.push({ grund: ergebnis.grund, roh: ergebnis.roh });
            continue;
        }
        if (gesehen.has(ergebnis.fingerabdruck)) {
            dubletten += 1;
            continue;
        }
        gesehen.add(ergebnis.fingerabdruck);
        angenommen.push(ergebnis.befund);
    }
    return { angenommen, verworfen, dubletten };
}
//# sourceMappingURL=durchsicht-befund.js.map